import React, { useState } from "react";
import { WYSIWYGEditorInput } from "./WYSIWYGEditor";
import {
  ArrowDownCircleIcon,
  ArrowUpCircleIcon,
  EyeIcon,
  EyeSlashIcon,
  LockClosedIcon,
  LockOpenIcon,
  TrashIcon,
} from "@heroicons/react/24/solid";
import { InputCheckboxButton, TextInput } from "../textInput";
import Modal from "../modal/Modal";
import ToolTip from "../tooltip/Tooltip";
import "./SectionEditor.css";

interface SectionEditorProps {
  header: string;
  content?: string;

  // Unique identifier for this section - used to create unique field names in react-final-form
  sectionId?: string;

  hideSideControls?: boolean;

  hideToolbar?: boolean;

  // navigation checkbox
  hideNavigationCheckbox?: boolean;
  addToNavigation?: boolean;
  showNavToggleDisabled?: boolean;
  onShowInNavigationCheckboxToggle?: () => void;

  // section locking
  locked?: boolean;
  editLocked?: boolean;
  showLockIcon?: boolean;
  placeholder?: string;
  required?: boolean;

  // section visibility
  isVisible?: boolean;
  showVisibilityToggle?: boolean;
  onVisibilityToggle?: () => void;

  onInputChange?: (payload: { header?: string; content?: string }) => void;
  onPositionUp?: () => void;
  onPositionDown?: () => void;
  onDelete?: () => void;
  onUnlock?: () => void;
  onLock?: () => void;
  setContent?: (html: string) => void;
  // disabling operations is determined by parent, which has more context on when to forbid moving, deletion, etc.
  headerDisabled?: boolean;
  moveDisabled?: boolean;
  moveUpDisabled?: boolean;
  moveDownDisabled?: boolean;
  deleteDisabled?: boolean;
  contentDisabled?: boolean; // RedactorX has no way of officially disabling the editor, see https://stackoverflow.com/a/38765457
  onRestore?: () => void;
  enablePrivacyCenterToolbar?: boolean;
  availablePrivacyCenterEmbeddedAssets?: Map<string, boolean>;
}

/**
 * Editable UI Container representing a distinct section of a Notice
 *
 * SectionEditor must sit inside a Form from `react-final-form`!
 */
export const SectionEditor = ({
  header,
  content,
  sectionId,
  locked,
  onUnlock,
  onLock,
  showLockIcon = true,
  hideSideControls = false,
  hideToolbar = false,
  hideNavigationCheckbox = false,
  addToNavigation,
  placeholder,
  onInputChange,
  onPositionUp,
  onPositionDown,
  onDelete,
  headerDisabled,
  moveDisabled,
  moveUpDisabled,
  moveDownDisabled,
  deleteDisabled,
  showNavToggleDisabled,
  editLocked,
  contentDisabled,
  setContent,
  onRestore,
  onShowInNavigationCheckboxToggle,
  isVisible,
  showVisibilityToggle = false,
  onVisibilityToggle,
  enablePrivacyCenterToolbar,
  availablePrivacyCenterEmbeddedAssets,
}: SectionEditorProps) => {
  const iconStateColorClasses = `disabled:text-neutral-400 hover:text-primary-600 text-neutral-500 [&:not(:disabled)]:hover:text-primary-600
`;
  placeholder = placeholder ?? ""; // placeholder is nullable

  const [showDeleteSectionConfirmationModal, setShowDeleteSectionConfirmationModal] =
    useState(false);

  const setContentInner = (content: string) => {
    if (setContent) {
      setContent(content);
    }
  };

  const onUpdateContent = (text: string) => {
    if (onInputChange) {
      onInputChange({ content: text });
    }
  };

  return (
    <>
      <div>
        <div style={{ display: "flex" }}>
          <div
            className={`section-editor-wrapper`}
            style={isVisible === false ? { filter: "blur(2px)" } : undefined}
          >
            <div className="tw-boundary">
              <div>
                <TextInput
                  field={sectionId ? `section-header-${sectionId}` : "section-header"}
                  label="Section Header"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    if (onInputChange) {
                      onInputChange({ header: e.currentTarget.value });
                    }
                  }}
                  required
                  disabled={editLocked || headerDisabled || locked}
                  placeholder="For example: Introduction, or Data We Share"
                  defaultValue={header}
                  maxLength={90}
                  trailingChildren={
                    hideNavigationCheckbox === true
                      ? undefined
                      : [
                          <InputCheckboxButton
                            key={1}
                            text="Show In Navigation"
                            checked={addToNavigation}
                            disabled={editLocked || showNavToggleDisabled || locked}
                            onClick={onShowInNavigationCheckboxToggle}
                          />,
                        ]
                  }
                />
              </div>
            </div>

            <div style={{ marginTop: hideToolbar ? "30px" : "6px" }}>
              {content !== null && (
                <div className="focus:[&_*]:outline-none">
                  <label
                    htmlFor="content"
                    style={{
                      display: "block",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      color: "#111827",
                      lineHeight: 1.5,
                    }}
                  >
                    Section Content
                  </label>
                  <WYSIWYGEditorInput
                    field="content"
                    value={content}
                    onChange={(content) => {
                      setContentInner(content);
                      onUpdateContent(content);
                    }}
                    onBlur={(_, content) => {
                      setContentInner(content);
                      onUpdateContent(content);
                    }}
                    toolbar={!hideToolbar}
                    minHeight={hideToolbar ? "320px" : "300px"}
                    maxHeight={hideToolbar ? "320px" : "300px"}
                    placeholder={placeholder}
                    disable={editLocked || contentDisabled || locked}
                    enablePrivacyCenterToolbar={enablePrivacyCenterToolbar}
                    availablePrivacyCenterEmbeddedAssets={availablePrivacyCenterEmbeddedAssets}
                  />
                  {!(editLocked || contentDisabled) && onRestore && (
                    <div>
                      <a
                        className="block text-sm font-medium leading-6 text-primary-900 underline"
                        onClick={onRestore}
                      >
                        Restore to Default Text
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {hideSideControls !== true && (
            <div className="tw-boundary">
              <div className="flex w-7 pl-2.5 flex-col">
                {showVisibilityToggle && (
                  <ToolTip
                    text={
                      isVisible
                        ? "This notice is visible in your Privacy Center. Click to hide it."
                        : "This notice is hidden in your Privacy Center. Click to show it."
                    }
                    width="250px"
                    xOffset={-60}
                    yOffset={75}
                  >
                    <div
                      className={`${iconStateColorClasses} cursor-pointer`}
                      onClick={onVisibilityToggle}
                    >
                      {" "}
                      {isVisible ? (
                        <EyeIcon className={`h-6 w-6 mt-6`} />
                      ) : (
                        <EyeSlashIcon className={`h-6 w-6 mt-6`} />
                      )}{" "}
                    </div>
                  </ToolTip>
                )}
                {(editLocked || locked) && showLockIcon && (
                  <ToolTip
                    text={
                      "This notice is generated by TrueVault based on your Data Map. Click to override and edit."
                    }
                    width="250px"
                    xOffset={-60}
                    yOffset={100}
                  >
                    <div className={`${iconStateColorClasses} cursor-pointer`} onClick={onUnlock}>
                      {" "}
                      <LockClosedIcon className={`h-6 w-6`} />{" "}
                    </div>
                  </ToolTip>
                )}
                {onLock && showLockIcon && (
                  <ToolTip
                    text={"Restore TrueVault's locked notice."}
                    width="250px"
                    xOffset={-60}
                    yOffset={100}
                  >
                    <div className={`${iconStateColorClasses} cursor-pointer`} onClick={onLock}>
                      {" "}
                      <LockOpenIcon className={`h-6 w-6`} />{" "}
                    </div>
                  </ToolTip>
                )}
                {showDeleteSectionConfirmationModal && (
                  <Modal
                    closeButton
                    firstButton="Cancel"
                    secondButton="Delete Section"
                    secondButtonColor="warning"
                    onSecondButtonClick={() => {
                      if (onDelete) onDelete();
                      setShowDeleteSectionConfirmationModal(false);
                    }}
                    onClose={() => setShowDeleteSectionConfirmationModal(false)}
                    onFirstButtonClick={() => setShowDeleteSectionConfirmationModal(false)}
                    showOverlay
                    alignButton="right"
                    title="Are you sure?"
                    body="Once you delete a section, it cannot be undone."
                    alignText="left"
                  />
                )}
                {!locked && !editLocked && (
                  <div className="flex flex-col justify-between h-full">
                    {onDelete && (
                      <ToolTip text="Delete section." width="250px" xOffset={-60} yOffset={100}>
                        <button
                          onClick={() => setShowDeleteSectionConfirmationModal(true)}
                          disabled={deleteDisabled}
                          className={iconStateColorClasses}
                          type="button"
                        >
                          <TrashIcon className={`h-6 w-6`} />
                        </button>
                      </ToolTip>
                    )}
                    {onPositionUp && onPositionDown && (
                      <div className="mt-6">
                        <ToolTip text="Move up" width="250px" xOffset={-60} yOffset={100}>
                          <button
                            onClick={onPositionUp}
                            disabled={moveDisabled || moveUpDisabled}
                            className={`mu-1 ${iconStateColorClasses}`}
                            type="button"
                          >
                            <ArrowUpCircleIcon className={`h-6 w-6`} />{" "}
                          </button>
                        </ToolTip>
                        <ToolTip text="Move down" width="250px" xOffset={-60} yOffset={100}>
                          <button
                            onClick={onPositionDown}
                            disabled={moveDisabled || moveDownDisabled}
                            className={iconStateColorClasses}
                            type="button"
                          >
                            <ArrowDownCircleIcon className={`h-6 w-6`} />{" "}
                          </button>
                        </ToolTip>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SectionEditor;
